import { useId, useState } from "react";
import { CheckIcon, CircleIcon, ListTodoIcon } from "lucide-react";
import { ComposerBanner } from "./composer-notices/ComposerBanner";
import type { ComposerPlanProgress } from "./deriveComposerPlanProgress";

const statusLabels = { pending: "Pending", inProgress: "Running", completed: "Completed" };

/** Uses the shared upstream banner geometry for model steps, separate from board tasks. */
export function ComposerPlanProgressRow({ progress }: { readonly progress: ComposerPlanProgress }) {
  const [expanded, setExpanded] = useState(false);
  const listId = useId();
  return (
    <ComposerBanner.Attachment>
      <ComposerBanner.Root>
        <ComposerBanner.Row
          render={<button type="button" />}
          className="min-h-touch md:min-h-7"
          aria-expanded={expanded}
          aria-controls={expanded ? listId : undefined}
          aria-label={`Model plan: ${progress.completedSteps} of ${progress.totalSteps} complete. Current step: ${progress.step}`}
          onClick={() => setExpanded(!expanded)}
        >
          <ComposerBanner.Icon>
            <ListTodoIcon />
          </ComposerBanner.Icon>
          <ComposerBanner.Content>
            <span className="shrink-0 text-muted-foreground">Model plan</span>
            <span className="min-w-0 truncate">{progress.step}</span>
          </ComposerBanner.Content>
          <ComposerBanner.Actions>
            <ComposerBanner.Count>
              {progress.completedSteps}/{progress.totalSteps}
            </ComposerBanner.Count>
            <ComposerBanner.ToggleIcon expanded={expanded} />
          </ComposerBanner.Actions>
        </ComposerBanner.Row>
        {expanded ? (
          <ComposerBanner.Scroll>
            <ComposerBanner.Children render={<ul id={listId} aria-label="Model plan steps" />}>
              {progress.steps.map((step, index) => (
                <ComposerBanner.Row key={progress.stepKeys[index]} render={<li />}>
                  <ComposerBanner.Icon
                    className={
                      step.status === "completed"
                        ? "text-success"
                        : step.status === "inProgress"
                          ? "text-primary"
                          : undefined
                    }
                  >
                    {step.status === "completed" ? <CheckIcon /> : <CircleIcon />}
                  </ComposerBanner.Icon>
                  <ComposerBanner.Content className="whitespace-normal break-words">
                    {step.step}
                  </ComposerBanner.Content>
                  <ComposerBanner.Actions>
                    <span className="text-muted-foreground text-xs">
                      {statusLabels[step.status]}
                    </span>
                  </ComposerBanner.Actions>
                </ComposerBanner.Row>
              ))}
            </ComposerBanner.Children>
          </ComposerBanner.Scroll>
        ) : null}
      </ComposerBanner.Root>
    </ComposerBanner.Attachment>
  );
}
